import { useState, useEffect } from "react";
import { Box, Button, Input, Typography, InputAdornment } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { resetPassword } from "../api/authApi";

export const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [email, setEmail] = useState("");
    const [token, setToken] = useState("");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const emailParam = searchParams.get('email');
        const tokenParam = searchParams.get('token');

        if (emailParam) {
            setEmail(decodeURIComponent(emailParam));
        }

        if (tokenParam) {
            setToken(decodeURIComponent(tokenParam));
        }

        if (!tokenParam) {
            setAlertMessage("Invalid reset password link. Please try again.");
            setShowAlert(true);
        }
    }, [searchParams]);

    const handleToggleNewPassword = () => {
        setShowNewPassword((prevShow) => !prevShow);
    };

    const handleToggleConfirmPassword = () => {
        setShowConfirmPassword((prevShow) => !prevShow);
    };

    const validatePasswords = () => {
        const errors: string[] = [];

        // Check if fields are filled
        if (!newPassword || !confirmPassword) {
            errors.push("Please enter both passwords");
        }

        // Only validate password requirements if password is provided
        if (newPassword) {
            // Check minimum length
            if (newPassword.length < 8) {
                errors.push("Password must be at least 8 characters");
            }

            // Check for uppercase letter
            if (!/[A-Z]/.test(newPassword)) {
                errors.push("Password must contain at least one uppercase letter");
            }

            // Check for lowercase letter
            if (!/[a-z]/.test(newPassword)) {
                errors.push("Password must contain at least one lowercase letter");
            }

            // Check for digit
            if (!/\d/.test(newPassword)) {
                errors.push("Password must contain at least one digit");
            }

            // Check for punctuation/special character
            if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) {
                errors.push("Password must contain at least one special character");
            }
        }

        // Check if passwords match (only if both are provided)
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            errors.push("Passwords do not match");
        }

        if (errors.length > 0) {
            setAlertMessage(errors.join(" • "));
            return false;
        }

        return true;
    };

    const handleResetPassword = async () => {
        setShowAlert(false);
        if (!validatePasswords()) {
            setShowAlert(true);
            return;
        }

        if (!token) {
            toast.error("Invalid reset password link. Please try again.");
            return;
        }

        try {
            const response = await resetPassword({
                email: email,
                token: token,
                newPassword: newPassword
            });

            if (response.success) {
                toast.success("Your password has been reset successfully.");
                setTimeout(() => {
                    navigate("/login");
                }, 1000);
            } else {
                toast.error(response.errorMessage);
            }
        } catch (error) {
            console.error("Error resetting password:", error);
            toast.error("Something went wrong while resetting your password. Please try again or contact support.");
        }
    };

    return (
        <div className="auth" style={{ display: 'flex', height: '100vh' }}>
            <Box
                sx={{
                    background: "#f3f3f3",
                    borderRadius: 5,
                    width: 500,
                    margin: "auto",
                    fontSize: "16px",
                    py: 2,
                    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        py: 7,
                        textAlign: "center",
                        fontFamily: "YourFontName, sans-serif",
                        letterSpacing: "5px",
                    }}
                >
                    Reset Password
                </Typography>

                {email && (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "center",
                            color: "gray",
                            mb: 3,
                            fontSize: "14px",
                        }}
                    >
                        Resetting password for: {email}
                    </Typography>
                )}

                <Input
                    value={newPassword}
                    onChange={(e) => {
                        setShowAlert(false);
                        setNewPassword(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleResetPassword();
                    }}
                    type={showNewPassword ? "text" : "password"}
                    placeholder="New Password*"
                    color="warning"
                    sx={{
                        display: "flex",
                        margin: "auto",
                        width: 325,
                        mb: 3,
                        cursor: "pointer",
                    }}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton onClick={handleToggleNewPassword} edge="end">
                                {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    }
                />

                <Input
                    value={confirmPassword}
                    onChange={(e) => {
                        setShowAlert(false);
                        setConfirmPassword(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleResetPassword();
                    }}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password*"
                    color="warning"
                    sx={{
                        display: "flex",
                        margin: "auto",
                        width: 325,
                        cursor: "pointer",
                    }}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton onClick={handleToggleConfirmPassword} edge="end">
                                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    }
                />

                <Typography
                    variant="body2"
                    sx={{
                        textAlign: "left",
                        color: "gray",
                        fontSize: "12px",
                        width: 325,
                        margin: "10px auto 0 auto",
                        lineHeight: 1.4,
                    }}
                >
                    Password must contain:
                    <br />• At least 8 characters
                    <br />• One uppercase letter (A-Z)
                    <br />• One lowercase letter (a-z)
                    <br />• One digit (0-9)
                    <br />• One special character (!@#$%^&*()_+-=[]{'{}'}|;':",./&lt;&gt;?)
                </Typography>

                {showAlert && (
                    <div style={{
                        color: "red",
                        textAlign: "left",
                        marginTop: "20px",
                        width: 325,
                        margin: "20px auto 0 auto",
                        fontSize: "14px",
                        lineHeight: 1.4
                    }}>
                        {alertMessage.includes(" • ") ? (
                            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                {alertMessage.split(" • ").map((error, index) => (
                                    <li key={index} style={{ marginBottom: "4px" }}>
                                        {error}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div style={{ textAlign: "center" }}>{alertMessage}</div>
                        )}
                    </div>
                )}

                <div>
                    <Button
                        onClick={handleResetPassword}
                        sx={{ display: "flex", m: "auto", my: 4 }}
                        variant="contained"
                        color="primary"
                    >
                        Reset Password
                    </Button>
                </div>

                <ToastContainer style={{ marginTop: "3%" }} />

                <p
                    style={{
                        textAlign: "center",
                        fontSize: "18px",
                    }}
                >
                    Remember your password?
                    <Link
                        style={{ textDecoration: "none", color: "gray" }}
                        onMouseEnter={(e: any) => (e.target.style.color = "#1976d2")}
                        onMouseLeave={(e: any) => (e.target.style.color = "gray")}
                        to="/login"
                    >
                        {""} Back to Login
                    </Link>
                </p>
            </Box>
        </div>
    );
}; 